import orderStyles from '@crm/styles/order.module.css';
import {housekeeprApi} from '@crm/api/housekeepr-api';
import {skipToken} from '@reduxjs/toolkit/query';
import {useAppDispatch, useAppSelector} from '@crm/services/store';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown, faChevronUp} from '@fortawesome/free-solid-svg-icons';
import {CleaningServicePriceRequest} from '@crm/api/models/requests/housekeepr-api';
import {cleaningActions, cleaningSelectors} from '@crm/services/cleaning-slice';
import ExtraServicesPricesByFreq from '@crm/components/layout/landing-page/order-wizard/cleaning-prices/extra-services-by-freq';
import React from 'react';
import ExtraServicesPriceLabel from '@crm/components/layout/landing-page/order-wizard/cleaning-prices/extra-services-by-freq/price-label';
import {VAT} from '@crm/utils/constants';

export const CleaningPrice = () => {
    const dispatch = useAppDispatch();
    const selectedAddress = useAppSelector(cleaningSelectors.selectAddress);
    const selectedArea = useAppSelector(cleaningSelectors.selectArea);
    const selectedServiceId = useAppSelector(cleaningSelectors.selectServiceId);
    const selectedFrequencyId = useAppSelector(cleaningSelectors.selectFrequencyId);
    const selectedPaymentIntervalId = useAppSelector(cleaningSelectors.selectPaymentIntervalId);
    const selectedExtraServices = useAppSelector(cleaningSelectors.selectExtraServices);
    const verifiedCode = useAppSelector(cleaningSelectors.selectVerifiedCode);
    const isExpanded = useAppSelector(cleaningSelectors.selectPriceExpanded);

    const {data: addressResult} = housekeeprApi.useRegisterAddressQuery(
        selectedAddress ? {darId: selectedAddress.adresse.id} : skipToken
    );

    const {data: serviceResult} = housekeeprApi.useGetServiceQuery(selectedServiceId ?? skipToken);

    const frequency = serviceResult?.frequencies.find((f) => f.id == selectedFrequencyId);
    const isRecurring = frequency?.every !== 0;

    const {data: extraServicesResult, isFetching: extraServicesLoading} = housekeeprApi.useGetExtraServicesQuery(
        selectedServiceId ?? skipToken
    );

    const availableExtraServices = extraServicesResult?.filter((extraService) => extraService.is_orderable);

    const useCode = !!verifiedCode;

    const {data: codeResult} = housekeeprApi.useCheckCodeQuery(
        useCode && selectedServiceId && selectedFrequencyId
            ? {
                  code: verifiedCode,
                  service_id: selectedServiceId,
                  frequency_id: selectedFrequencyId,
              }
            : skipToken
    );

    const canSearchPrice = !!selectedServiceId && !!addressResult && !!frequency;

    const priceRequest = canSearchPrice
        ? ({
              service_id: selectedServiceId,
              address_id: addressResult.id,
              area: selectedArea,
          } as CleaningServicePriceRequest)
        : undefined;

    const {data: paymentIntervalsResult} = housekeeprApi.useGetPaymentIntervalsQuery(
        isRecurring ? undefined : skipToken
    );

    const paymentInterval = paymentIntervalsResult?.find((p) => p.id == selectedPaymentIntervalId);

    const {data: priceResult, isFetching: isFetchingPrice} = housekeeprApi.useGetServicePriceQuery(
        priceRequest ?? skipToken
    );

    if (!canSearchPrice || !priceResult || !serviceResult || (isRecurring && !paymentInterval)) {
        return <></>;
    }

    const anyIrregularExtraServices =
        selectedExtraServices !== undefined &&
        isRecurring &&
        selectedExtraServices.some((extraService) => extraService.how_often > 1);

    const servicePrice = priceResult;
    const extraServicePrice =
        selectedExtraServices !== undefined && selectedExtraServices.length >= 1 && availableExtraServices
            ? selectedExtraServices
                  .map((orderExtraService) => {
                      const extraService = availableExtraServices.find(
                          (extraService) => extraService.id == orderExtraService.id
                      );
                      const extraServicePrice = extraService?.price ?? 0;
                      return (
                          extraServicePrice * orderExtraService.count * (1 / Math.max(1, orderExtraService.how_often))
                      );
                  })
                  .reduce((sum, price) => sum + price, 0)
            : 0;

    const craftsmanDeductionMultiplier = 1 - serviceResult.craftsman_deduction / 100;
    const paymentIntervalDiscountMultiplier = 1 - (paymentInterval?.discount ?? 0) / 100;
    const codeDiscount = useCode
        ? servicePrice * ((codeResult?.discount_code?.percent_off ?? 0) / 100) + (codeResult?.discount_code?.value ?? 0)
        : 0;

    const averageFullPrice = servicePrice * VAT + extraServicePrice * VAT - codeDiscount * VAT;

    const averagePrice = averageFullPrice * paymentIntervalDiscountMultiplier * craftsmanDeductionMultiplier;

    const formattedAveragePrice = averagePrice.toLocaleString('da-DK', {
        style: 'currency',
        currency: 'DKK',
    });

    return (
        <>
            <div className={`${orderStyles.priceDetails} ${isExpanded ? orderStyles.expanded : ''}`}>
                <h4 className={orderStyles.header}>Prisoversigt</h4>
                <ExtraServicesPricesByFreq
                    howOften={1}
                    basePrice={priceResult}
                    craftsmanDeduction={serviceResult.craftsman_deduction}
                    paymentInterval={paymentInterval}
                    extraServices={selectedExtraServices}
                    service={serviceResult}
                    frequency={frequency}
                    discountCode={useCode ? codeResult?.discount_code : undefined}
                />
                <ExtraServicesPricesByFreq
                    howOften={2}
                    basePrice={priceResult}
                    craftsmanDeduction={serviceResult?.craftsman_deduction ?? 26}
                    paymentInterval={paymentInterval}
                    extraServices={selectedExtraServices}
                    service={serviceResult}
                    frequency={frequency}
                    discountCode={useCode ? codeResult?.discount_code : undefined}
                />
                <ExtraServicesPricesByFreq
                    howOften={4}
                    basePrice={priceResult}
                    craftsmanDeduction={serviceResult?.craftsman_deduction ?? 26}
                    paymentInterval={paymentInterval}
                    extraServices={selectedExtraServices}
                    service={serviceResult}
                    frequency={frequency}
                    discountCode={useCode ? codeResult?.discount_code : undefined}
                />
                {anyIrregularExtraServices && availableExtraServices && (
                    <div className='flex justify-between pb-2'>
                        <ExtraServicesPriceLabel style={{paddingRight: 15}}>
                            {'Gennemsnitlig pris pr. besøg (efter servicefradrag)'}
                        </ExtraServicesPriceLabel>
                        <ExtraServicesPriceLabel>{formattedAveragePrice}</ExtraServicesPriceLabel>
                    </div>
                )}
            </div>
            <div
                className={`${orderStyles.priceFooter}`}
                onClick={() => dispatch(cleaningActions.togglePriceExpanded())}>
                <h4 className={orderStyles.header} style={{alignSelf: 'flex-start'}}>
                    Din pris efter fradrag og rabat
                </h4>
                <div className={orderStyles.footerContent}>
                    {isFetchingPrice || extraServicesLoading ? (
                        <div style={{display: 'flex'}}>
                            <div className={orderStyles.spinnerContainer}>
                                <div className={orderStyles.spinner}></div>
                            </div>
                            <p className='ml-2'>Indlæser pris...</p>
                        </div>
                    ) : (
                        <div>
                            {!isRecurring
                                ? `${formattedAveragePrice}`
                                : anyIrregularExtraServices
                                ? `${formattedAveragePrice} i gns. per gang`
                                : `${formattedAveragePrice} per gang`}
                        </div>
                    )}
                    <div
                        className={`${orderStyles.button} ${orderStyles.flexRow} ${orderStyles.flexMainEnd}`}
                        style={{gap: '8px'}}>
                        {isExpanded ? 'Luk' : 'Se mere'}
                        <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronUp}
                            size='xs'
                            fixedWidth={true}
                            style={{width: 20, height: 20}}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CleaningPrice;
